import { FormEvent, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup
} from "firebase/auth";
import { LockKeyhole, X } from "lucide-react";
import { auth, firebaseConfigured } from "../lib/firebase";

type Mode = "sign-in" | "join" | "reset";

export function AuthDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<Mode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError("");
    setNotice("");
    try { await action(); } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The account service could not complete that request.");
    } finally { setBusy(false); }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const currentAuth = auth;
    if (!currentAuth) return;
    await run(async () => {
      if (mode === "reset") {
        await sendPasswordResetEmail(currentAuth, email.trim());
        setNotice("If the address has an account, a reset message is on its way.");
        return;
      }
      if (mode === "join") {
        const result = await createUserWithEmailAndPassword(currentAuth, email.trim(), password);
        await sendEmailVerification(result.user);
        setNotice("Account created. Verify your email before voting or suggesting.");
        return;
      }
      await signInWithEmailAndPassword(currentAuth, email.trim(), password);
      onClose();
    });
  }

  async function googleSignIn() {
    const currentAuth = auth;
    if (!currentAuth) return;
    await run(async () => {
      await signInWithPopup(currentAuth, new GoogleAuthProvider());
      onClose();
    });
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="icon-button dialog-close" onClick={onClose} aria-label="Close account dialog"><X /></button>
        <LockKeyhole className="dialog-mark" aria-hidden="true" />
        <span className="eyebrow">Mashr consumer account</span>
        <h2 id="auth-title">{mode === "join" ? "Create an account" : mode === "reset" ? "Reset password" : "Sign in to participate"}</h2>
        <p>Roadmaps are public. Your verified Mashr account is required only to vote or suggest.</p>

        {!firebaseConfigured ? (
          <div className="status-panel error" role="alert">Account registration is not configured in this environment.</div>
        ) : (
          <>
            <form className="auth-form" onSubmit={submit}>
              <label>Email<input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
              {mode !== "reset" ? <label>Password<input type="password" minLength={6} autoComplete={mode === "join" ? "new-password" : "current-password"} required value={password} onChange={(event) => setPassword(event.target.value)} /></label> : null}
              <button className="button primary" disabled={busy}>{busy ? "Please wait…" : mode === "join" ? "Create account" : mode === "reset" ? "Send reset email" : "Sign in"}</button>
            </form>
            {mode !== "reset" ? (
              <div className="provider-buttons">
                <button className="button secondary" disabled={busy} onClick={googleSignIn}>Continue with Google</button>
              </div>
            ) : null}
          </>
        )}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        {notice ? <p className="form-notice" role="status">{notice}</p> : null}
        <div className="dialog-actions">
          {mode !== "sign-in" ? <button onClick={() => setMode("sign-in")}>Sign in</button> : null}
          {mode !== "join" ? <button onClick={() => setMode("join")}>Create account</button> : null}
          {mode !== "reset" ? <button onClick={() => setMode("reset")}>Forgot password?</button> : null}
        </div>
        <p className="fine-print"><LockKeyhole size={14} /> This is the shared Mashr product identity; repository access is not required.</p>
      </section>
    </div>
  );
}
